import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// helpers
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { shouldShowMilesAndMore } from "../../../../common/utils/transport/airlineBookingPayload";
import { getDeutscheBahnOfferPriceLabel } from "../helpers/deutscheBahnPassengerTypes";
import { resolveDeutscheBahnSummaryPassengers } from "../helpers/resolveDeutscheBahnSummaryPassenger";
import type { DeutscheBahnBookingForm, DeutscheBahnOfferPrice } from "../types";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import MilesAndMoreInfo from "../../../../components/MilesAndMoreInfo";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";
// actions
import { useActions } from "../../../../common/hooks/useActions";
import { deutscheBahnActionCreators } from "../deutscheBahnModule";
import { errorFieldActionCreators } from "../../../../common/store/modules/errorFieldModule";

interface DeutscheBahnSummaryProps {
  offerName?: string | null;
  /** Resolves the passenger type labels (offer's `offer_prices`). */
  offerPrices: DeutscheBahnOfferPrice[];
  onBuyTicket: () => void;
  cancellationPolicy?: string | null;
}

export default function DeutscheBahnSummary({
  offerName,
  offerPrices,
  onBuyTicket,
  cancellationPolicy
}: DeutscheBahnSummaryProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";

  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const prebookPriceInfo = useSelector((state: any) => state.deutscheBahn.prebookPriceInfo);
  const dataForBooking = useSelector((state: any) => state.deutscheBahn.dataForBooking) as DeutscheBahnBookingForm;
  const passengerList = useSelector((state: any) => state.deutscheBahn.passengerList);
  const errorField = useSelector((state: any) => state.errorField.errorField);
  const platformType = useSelector((state: any) => state.global.platformType);

  const updateFormForBooking = useActions(deutscheBahnActionCreators?.updateFormForBookingAction, []);
  const resetPrebookDataInfo = useActions(deutscheBahnActionCreators?.resetPrebookDataInfoAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);

  const isAirline = platformType === "airline";
  const summaryPassengers = resolveDeutscheBahnSummaryPassengers(prebookPriceInfo?.details?.passengers, passengerList);

  useEffect(() => {
    return () => {
      resetErrorField();
    };
  }, [resetErrorField]);

  const handleBookingFormInputChange = (e: any, field: keyof DeutscheBahnBookingForm) => {
    updateFormForBooking({ ...dataForBooking, [field]: e.target.value });
  };

  const handleEditTicketOptionButton = () => {
    if (window.confirm(t("transport.editOptionText"))) {
      resetPrebookDataInfo();
    }
  };

  return (
    prebookPriceInfo && (
      <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
            <Box>
              <Box className="summary-option-title" mb={2}>
                {t("transport.ticketOption")}
              </Box>
              <Box className="summary-option-title default-text-transform">{offerName}</Box>
            </Box>
            <Grid
              item
              sx={{ width: "auto", height: "40px", alignSelf: { xs: "flex-start", sm: "auto" } }}
              mt={{ xs: 3, sm: 0 }}
            >
              <AppButton
                label={t("transport.editBtn")}
                onClick={handleEditTicketOptionButton}
                disableRipple={true}
                customBackgroundColor={theme.palette.primary.dark}
                customHoverColor={theme.palette.primary.dark}
                fontSize="14px"
                dataAttribute="deutsche-bahn-edit-option"
              />
            </Grid>
          </Box>
          <Grid container className="summary-passenger-box" mt={2} mb={2} pb={2} pt={2}>
            {summaryPassengers.map((summaryPassenger, index) => (
              <Box
                key={index}
                data-cy="deutsche-bahn-summary-passenger"
                sx={{
                  display: "flex",
                  textAlign: "center",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%"
                }}
              >
                <Grid item xs={6} pl={{ xs: 1, sm: 0 }}>
                  {summaryPassenger.firstName} {summaryPassenger.lastName}
                </Grid>
                <Grid item xs={6}>
                  {getDeutscheBahnOfferPriceLabel(offerPrices, summaryPassenger.passengerType)}
                </Grid>
              </Box>
            ))}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
              {t("transport.totalPrice")}: {prebookPriceInfo?.formatted_price}
            </Box>
          </Box>
          <Box mt={1.5} className="summary-footnotes">
            <Box>{t("transport.deutscheBahn.groupPriceNote")}</Box>
            <Box>{t("transport.deutscheBahn.photoIdNote")}</Box>
          </Box>
        </Box>
        <Grid container mt={2}>
          <Grid item xs={12} md={6}>
            <Grid container width={{ sm: "100%", md: "100%" }}>
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestNameLabel")} *</Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"nameForm"}
                    type="string"
                    value={dataForBooking?.nameForm}
                    onChange={event => handleBookingFormInputChange(event, "nameForm")}
                    error={errorField?.nameForm}
                    dataAttribute="deutsche-bahn-name"
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestEmailLabel")} *</Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"emailForm"}
                    type="string"
                    value={dataForBooking?.emailForm}
                    onChange={event => handleBookingFormInputChange(event, "emailForm")}
                    error={errorField?.emailForm}
                    dataAttribute="deutsche-bahn-email"
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
                <Grid item xs={3}>
                  <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.commentLabel")}</Box>
                </Grid>
                <Grid item xs={9} ml={1}>
                  <AppFormInput
                    name={"commentForm"}
                    type="string"
                    value={dataForBooking?.commentForm}
                    onChange={event => handleBookingFormInputChange(event, "commentForm")}
                    error={errorField?.commentForm}
                    dataAttribute="deutsche-bahn-comment"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {shouldShowMilesAndMore(prebookPriceInfo?.miles_and_more, isAirline) && (
            <Grid
              item
              xs={12}
              md={6}
              pl={{ xs: 0, md: 5 }}
              pt={{ xs: 4, md: 0 }}
              sx={{ alignItems: "center", display: "flex" }}
            >
              <MilesAndMoreInfo miles={prebookPriceInfo?.miles_and_more} />
            </Grid>
          )}
        </Grid>

        {cancellationPolicy && (
          <Box pt={6}>
            <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
          </Box>
        )}
        <Grid
          item
          xs={12}
          sx={{
            width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
            margin: cancellationPolicy?.trim() ? "24px auto 0 auto" : "60px auto 0 auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 2 }
          }}
        >
          {isAirline && (
            <AppButton
              label={t("transport.goBack")}
              onClick={() => navigateBackOrToMain(navigate, { language })}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
              dataAttribute="deutsche-bahn-go-back-2"
            />
          )}
          <AppButton
            label={t("transport.buyTicketsBtn")}
            onClick={onBuyTicket}
            disableRipple={true}
            customBackgroundColor={tenantInfo?.style?.button?.color}
            customActiveColor={tenantInfo?.style?.button?.active}
            customHoverColor={tenantInfo?.style?.button?.hover}
            customClickColor={tenantInfo?.style?.button?.click}
            fontSize="14px"
            dataAttribute="deutsche-bahn-buy-tickets"
          />
        </Grid>
      </Box>
    )
  );
}
